import { useRef, useState } from "react";
import { toast } from "sonner";
import { X, UploadSimple, FileXls, DownloadSimple } from "@phosphor-icons/react";
import { api, apiError } from "../../lib/api";
import { TemplateEditor } from "./TemplateEditor";
import { btnPrimary, btnGhost } from "./shared";

const ROLE_LABEL = { title: "Task name", description: "Description", boolean: "Yes / No", numeric: "Reading", min: "Min", max: "Max",
  target: "Target range", unit: "Unit", photo: "Photo flag", text: "Remarks", verify: "Verified by (ignored)", shift: "Shift", section: "Section",
  id: "Row no. (ignored)", frequency: "Frequency (ignored)", "": "Not used" };

export async function downloadSample() {
  try {
    const { data } = await api.get("/checklists/import/sample", { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = "practitality-checklist-sample.csv"; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error(apiError(e)); }
}

export function ImportWizard({ config, departments, venues, onSaved, onClose }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [parsed, setParsed] = useState(null);
  const fileRef = useRef(null);

  const parse = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/checklists/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!data.items.length) toast.warning("No checklist lines were detected — review the sheet layout");
      setParsed(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (parsed) {
    const initial = {
      name: parsed.suggested_name || file.name.replace(/\.[^.]+$/, ""), category: parsed.suggested_category || "custom",
      shifts: parsed.suggested_shifts?.length ? parsed.suggested_shifts : ["morning"], items: parsed.items,
      source: { type: "upload", filename: parsed.filename, sheet: parsed.sheet, header_row: parsed.header_row },
    };
    const header = (
      <div data-testid="import-review" className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="font-bold text-sm flex items-center gap-2"><FileXls size={18} className="text-emerald-600" /> {parsed.filename}{parsed.sheet !== "csv" ? ` · sheet "${parsed.sheet}"` : ""} · header row {parsed.header_row} · {parsed.rows_total} rows read</p>
          <button data-testid="import-back-button" onClick={() => setParsed(null)} className="text-xs font-bold text-slate-500 hover:text-slate-900">← Choose another file</button>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="import-column-map">
          {parsed.columns.filter((c) => c.header).map((c) => (
            <span key={c.index} className={`text-[11px] font-semibold px-2 py-1 rounded-md border ${c.role ? "bg-white border-slate-300 text-slate-700" : "bg-slate-100 border-slate-200 text-slate-400"}`}>
              {c.header} <span className="text-slate-400">→</span> <span className={c.role ? "text-sky-700" : ""}>{ROLE_LABEL[c.role] ?? c.role}</span>
            </span>
          ))}
        </div>
        {parsed.warnings.map((w, i) => <p key={i} data-testid={`import-warning-${i}`} className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">{w}</p>)}
        <p className="text-xs text-slate-500">Review every line below — correct the proof type, set safe ranges, then pick departments and shifts before activating.</p>
      </div>
    );
    return <TemplateEditor initial={initial} config={config} departments={departments} venues={venues} onSaved={onSaved} onClose={onClose} headerNode={header} />;
  }

  return (
    <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={onClose}>
      <div data-testid="import-wizard" onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-lg rounded-2xl p-6 sm:p-8">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl sm:text-2xl font-bold">Import log sheet</h2>
          <button data-testid="import-close" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X size={22} /></button>
        </div>
        <p className="text-sm text-slate-500 mb-4">Upload your existing HACCP / kitchen audit sheet (.xlsx or .csv). Columns such as task name, Yes/No, readings, target range, photo and remarks are detected automatically — you review the mapping before anything is saved.</p>
        <button data-testid="import-dropzone" onClick={() => fileRef.current?.click()}
          className="w-full h-36 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-2 text-slate-500 font-bold hover:border-slate-900 bg-slate-50">
          <UploadSimple size={28} />
          {file ? <span className="text-slate-900">{file.name} <span className="text-slate-400 font-semibold">({Math.round(file.size / 1024)} KB)</span></span> : "Tap to choose .xlsx / .csv"}
        </button>
        <input ref={fileRef} data-testid="import-file-input" type="file" accept=".xlsx,.xlsm,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <div className="flex items-center justify-between mt-5 gap-2 flex-wrap">
          <button data-testid="template-sample-button" onClick={downloadSample} className="text-xs font-bold text-slate-500 hover:text-slate-900 flex items-center gap-1"><DownloadSimple size={14} /> Download sample CSV</button>
          <div className="flex gap-2">
            <button onClick={onClose} className={btnGhost}>Cancel</button>
            <button data-testid="import-parse-button" onClick={parse} disabled={!file || busy} className={btnPrimary}>{busy ? "Reading sheet…" : "Detect columns"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
